/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
  useBatchingCallback,
  useCombinedRefs,
} from '@web-stories-wp/react';
import classnames from 'classnames';
import { useUnits } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { useStory, useCanvas, useLayout } from '../../../app';
import elementIs from '../../../elements/utils/elementIs';
import Moveable from '../../moveable';
import objectWithout from '../../../utils/objectWithout';
import { noop } from '../../../utils/noop';
import { useTransform } from '../../transform';
import useSnapping from '../utils/useSnapping';
import useUpdateSelectionRectangle from '../utils/useUpdateSelectionRectangle';
import useWindowResizeHandler from '../useWindowResizeHandler';
import {
  getLegacyOverlayFromStack,
  getOverlayStack,
} from '../../../utils/overlayStack';
import { isTextBoxAttachment } from '../../../utils/textBoxAttachment';
import useDrag from './useDrag';
import useResize from './useResize';
import useRotate from './useRotate';
import FilterCanvasIndicator from '../filterCanvasIndicator';

function SingleSelectionMoveable({
  selectedElement,
  targetEl,
  pushEvent,
  isEditMode,
  editMoveableRef,
  onContextMenu,
  hideMoveable = false,
}) {
  const moveable = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const {
    duplicateDragData,
    nodesById,
    canvasContainer,
    activeFilterId,
    requestFilterPicker,
    setDuplicateDragData,
    shouldDuplicateOnDrag,
    shouldThrottleDragRotation,
    shouldDuplicateAndThrottle,
  } = useCanvas(
    ({
      actions: { requestFilterPicker = noop, setDuplicateDragData },
      state: {
        duplicateDragData,
        nodesById,
        canvasContainer,
        activeFilterId,
        shouldDuplicateOnDrag,
        shouldThrottleDragRotation,
        shouldDuplicateAndThrottle,
      },
    }) => ({
      duplicateDragData,
      nodesById,
      canvasContainer,
      activeFilterId,
      requestFilterPicker,
      setDuplicateDragData,
      shouldDuplicateOnDrag,
      shouldThrottleDragRotation,
      shouldDuplicateAndThrottle,
    })
  );
  const { getBox } = useUnits(({ actions: { getBox } }) => ({
    getBox,
  }));
  const {
    actions: { pushTransform },
  } = useTransform();
  const { zoomSetting, scrollLeft, scrollTop } = useLayout(
    ({ state: { zoomSetting, scrollLeft, scrollTop } }) => ({
      zoomSetting,
      scrollLeft,
      scrollTop,
    })
  );

  const actionsEnabled =
    !selectedElement.isBackground &&
    !selectedElement.isLocked &&
    !selectedElement.isHidden;
  const isPositionLocked = isTextBoxAttachment(
    selectedElement.textBoxAttachment
  );
  const canDrag = actionsEnabled && !isPositionLocked;
  const shouldShowFilterIndicator =
    !isEditMode && !selectedElement.isLocked && !selectedElement.isHidden;

  const latestEvent = useRef();

  const { backgroundElement, updateElementsById } = useStory(
    ({ actions, state: { currentPage } }) => ({
      backgroundElement:
        currentPage.elements.find(
          (element) => elementIs.backgroundable(element) && element.isBackground
        ) ?? {},
      updateElementsById: actions.updateElementsById,
    })
  );

  useWindowResizeHandler(moveable);

  useEffect(() => {
    latestEvent.current = pushEvent;
  }, [pushEvent]);

  // If scroll ever updates, update rect now
  useEffect(() => {
    if (!moveable.current) {
      return;
    }
    moveable.current.updateRect();
  }, [scrollLeft, scrollTop]);

  // If zoom ever updates, update selection rect
  useUpdateSelectionRectangle(moveable, [zoomSetting]);

  useEffect(() => {
    if (!moveable.current) {
      return;
    }
    // If we have persistent event then let's use that, ensuring the targets match.
    if (
      latestEvent.current &&
      targetEl.contains(latestEvent.current.target) &&
      canDrag
    ) {
      moveable.current.moveable.dragStart(latestEvent.current);
    }
    moveable.current.updateRect();
  }, [targetEl, moveable, canDrag]);

  // Update moveable with whatever properties could be updated outside moveable
  // itself.
  useEffect(() => {
    if (!moveable.current) {
      return;
    }
    moveable.current.updateRect();
  });

  const box = getBox(selectedElement);
  const frame = useMemo(
    () => ({
      translate: [0, 0],
      rotate: box.rotationAngle,
      resize: [0, 0],
      updates: null,
    }),
    [box.rotationAngle]
  );

  const setTransformStyle = useCallback(
    (target, newFrame) => {
      // Get the changes coming from each action type.
      Object.keys(newFrame).forEach((key) => {
        frame[key] = newFrame[key];
      });
      target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;
      if (frame.resize[0]) {
        target.style.width = `${frame.resize[0]}px`;
      }
      if (frame.resize[1]) {
        target.style.height = `${frame.resize[1]}px`;
      }
      pushTransform(selectedElement.id, frame);
    },
    [frame, pushTransform, selectedElement.id]
  );

  /**
   * Resets Moveable once the action is done, sets the initial values.
   *
   * @param {Object} target Target element.
   */
  const resetMoveable = useBatchingCallback(
    (target) => {
      frame.direction = [0, 0];
      frame.translate = [0, 0];
      frame.resize = [0, 0];
      frame.updates = null;
      pushTransform(selectedElement.id, null);
      // Inline start resetting has to be done very carefully here to avoid
      // conflicts with stylesheets. See #3951.
      target.style.transform = '';
      target.style.width = '';
      target.style.height = '';
      if (moveable.current) {
        moveable.current.updateRect();
      }
    },
    [frame, pushTransform, selectedElement.id]
  );

  const canSnap = !isEditMode && actionsEnabled;
  const hideHandles = isDragging;

  const classNames = classnames('default-moveable', {
    'hide-handles': hideHandles,
    'type-text': selectedElement.type === 'text',
    immoveable: selectedElement.isLocked || selectedElement.isHidden,
  });
  const _dragProps = useDrag({
    setIsDragging,
    resetMoveable,
    selectedElement,
    setTransformStyle,
    shouldDuplicateOnDrag: shouldDuplicateOnDrag || shouldDuplicateAndThrottle,
    shouldThrottleDrag:
      shouldThrottleDragRotation || shouldDuplicateAndThrottle,
    frame,
  });
  // No dragging in edit mode.
  const dragProps =
    isEditMode || isPositionLocked
      ? {
          onDrag: () => false,
          onDragEnd: () => false,
          onDragStart: () => false,
        }
      : _dragProps;

  const resizeProps = useResize({
    resetMoveable,
    selectedElement,
    setTransformStyle,
    frame,
    isEditMode,
    pushTransform,
    classNames,
  });

  const rotateProps = useRotate({
    selectedElement,
    isEditMode,
    pushTransform,
    frame,
    setTransformStyle,
    shouldThrottleRotation:
      shouldThrottleDragRotation || shouldDuplicateAndThrottle,
    resetMoveable,
  });

  // Get a list of all the other non-bg nodes
  const otherNodes = useMemo(
    () =>
      Object.values(
        objectWithout(nodesById, [selectedElement.id, backgroundElement.id])
      ),
    [backgroundElement.id, nodesById, selectedElement.id]
  );

  const snapProps = useSnapping({
    otherNodes,
    canSnap,
    isDragging,
  });

  const updateActiveFilterPattern = useCallback(
    (getNextPattern, { isPreview = false } = {}) => {
      if (!activeFilterId) {
        return;
      }

      if (isPreview) {
        const activeFilter = getOverlayStack(selectedElement).find(
          ({ id }) => id === activeFilterId
        );

        if (!activeFilter?.pattern) {
          return;
        }

        pushTransform(selectedElement.id, {
          color: getNextPattern(activeFilter.pattern),
          overlayFilterId: activeFilterId,
          staticTransformation: true,
          style: 'background',
        });
        return;
      }

      updateElementsById({
        elementIds: [selectedElement.id],
        properties: (element) => {
          const overlays = getOverlayStack(element);
          const nextFilters = overlays.map((filter) => {
            if (filter.id !== activeFilterId) {
              return filter;
            }

            return {
              ...filter,
              pattern: getNextPattern(filter.pattern),
            };
          });

          return {
            overlay: getLegacyOverlayFromStack(nextFilters),
            overlays: nextFilters,
          };
        },
      });
      pushTransform(selectedElement.id, null);
    },
    [activeFilterId, pushTransform, selectedElement, updateElementsById]
  );
  const resetActiveFilterPatternPreview = useCallback(
    () =>
      pushTransform(selectedElement.id, {
        overlayFilterId: activeFilterId,
        resetOverlayFilterPreview: true,
        staticTransformation: true,
        style: 'background',
      }),
    [activeFilterId, pushTransform, selectedElement.id]
  );

  useEffect(() => {
    if (!actionsEnabled || !moveable.current || !duplicateDragData) {
      return;
    }

    if (
      duplicateDragData.originalElId &&
      duplicateDragData.originalElId !== selectedElement.id
    ) {
      moveable.current.dragStart(duplicateDragData.inputEvent);
      setDuplicateDragData({ isDraggingDuplicate: true });
    }
  }, [
    actionsEnabled,
    duplicateDragData,
    selectedElement,
    setDuplicateDragData,
  ]);

  return (
    <>
      {shouldShowFilterIndicator && (
        <FilterCanvasIndicator
          activeFilterId={activeFilterId}
          box={box}
          element={selectedElement}
          onOpenPicker={requestFilterPicker}
          onResetPatternPreview={resetActiveFilterPatternPreview}
          onUpdatePattern={updateActiveFilterPattern}
          targetEl={targetEl}
          overlayTarget={canvasContainer}
        />
      )}
      {!hideMoveable && (
        <Moveable
          className={classNames}
          zIndex={0}
          ref={useCombinedRefs(moveable, editMoveableRef)}
          target={targetEl}
          edge
          draggable={canDrag}
          resizable={actionsEnabled && !hideHandles}
          rotatable={actionsEnabled && !hideHandles}
          {...dragProps}
          {...resizeProps}
          {...rotateProps}
          {...snapProps}
          onContextMenu={onContextMenu}
          origin={false}
          pinchable
        />
      )}
    </>
  );
}

SingleSelectionMoveable.propTypes = {
  selectedElement: PropTypes.object.isRequired,
  targetEl: PropTypes.object.isRequired,
  pushEvent: PropTypes.object,
  isEditMode: PropTypes.bool,
  editMoveableRef: PropTypes.object,
  onContextMenu: PropTypes.func,
  hideMoveable: PropTypes.bool,
};

export default SingleSelectionMoveable;
